<template>
  <!-- Operational notices. Deliberately a banner, not a modal: these are FYIs about things the
       user cannot act on, so they must not stand between them and the page. -->
  <div v-if="visibleNotices.length" class="notice_stack">
    <div
      v-for="notice in visibleNotices"
      :key="notice.notice_key"
      class="notice_bar"
      :class="'notice_bar--' + (notice.severity || 'info')"
      role="status"
    >
      <span class="notice_icon" aria-hidden="true">
        <i :class="iconFor(notice.severity)"></i>
      </span>
      <span class="notice_text">
        <b v-if="notice.title">{{ notice.title }}</b><template v-if="notice.title"> — </template>{{ notice.body }}
      </span>
      <button
        type="button"
        class="notice_dismiss"
        :title="dismissLabel"
        :aria-label="dismissLabel"
        @click="dismiss(notice)"
      >
        &times;
      </button>
    </div>
  </div>
</template>

<script>
import { fetchActiveNotices, isDismissed, rememberDismissal } from "../api/notices";

export default {
  name: "SystemNotices",
  data() {
    return {
      notices: [],
      dismissLabel: "Dismiss",
    };
  },
  computed: {
    visibleNotices() {
      // Dismissal is keyed to notice_key + version, so editing a notice (and bumping its
      // version) shows it again to people who had already dismissed the old wording.
      return this.notices.filter((notice) => !isDismissed(notice));
    },
  },
  methods: {
    iconFor(severity) {
      if (severity === "critical") return "bx bx-error-circle";
      if (severity === "warning") return "bx bx-error";
      return "bx bx-info-circle";
    },
    dismiss(notice) {
      rememberDismissal(notice);
      // Reassign rather than mutate so the computed re-evaluates.
      this.notices = this.notices.filter((n) => n.notice_key !== notice.notice_key);
    },
    async load() {
      this.notices = await fetchActiveNotices();
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
/* Matches the simulation hub's .mockup_banner — soft tint, thin border, no shouting — but a
   size down, because this sits above real page content rather than heading a mockup area. */
.notice_stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.notice_bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid #ffe082;
  background: #fff8e1;
  color: #5c4400;
  font-size: 13px;
  line-height: 1.45;
}

/* Only a genuine problem earns a different colour; info and warning share the quiet amber. */
.notice_bar--critical {
  border-color: #f5c6cb;
  background: #fdecea;
  color: #7d1f1f;
}

.notice_icon {
  font-size: 16px;
  flex-shrink: 0;
  line-height: 1;
}

.notice_text {
  flex: 1;
}

.notice_dismiss {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: none;
  color: inherit;
  opacity: 0.55;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: opacity 0.15s ease, background 0.15s ease;
}

.notice_dismiss:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.06);
}

.notice_dismiss:focus-visible {
  opacity: 1;
  outline: 2px solid currentColor;
  outline-offset: 1px;
}
</style>
